import type { FulfillmentItemType, OrderRecord, OrderStatus, PaymentChannel, PaymentStatus, FulfillmentStatus } from '@starkey/shared'

type StatusMeta = {
  label: string
  className: string
}

function withTone(label: string, tone: 'draft' | 'published' | 'sale' | 'used'): StatusMeta {
  return {
    label,
    className: `status-pill status-pill--${tone}`,
  }
}

export function getSimpleOrderStatus(order: OrderRecord) {
  if (order.orderStatus === 'CLOSED' || order.paymentStatus === 'CLOSED') {
    return 'CLOSED'
  }

  if (order.fulfillmentStatus === 'FULFILLED' || order.orderStatus === 'FULFILLED') {
    return 'FULFILLED'
  }

  if (order.paymentStatus === 'SUCCESS' || ['PAID', 'FULFILLING'].includes(order.orderStatus)) {
    return 'PAID'
  }

  return 'PENDING_PAYMENT'
}

export function getSimpleOrderStatusLabel(status: ReturnType<typeof getSimpleOrderStatus>) {
  switch (status) {
    case 'PAID':
      return '待发货'
    case 'FULFILLED':
      return '已发货'
    case 'CLOSED':
      return '已关闭'
    default:
      return '待支付'
  }
}

export function getSimpleOrderStatusMeta(order: OrderRecord) {
  const status = getSimpleOrderStatus(order)

  switch (status) {
    case 'PAID':
      return withTone('待发货', 'published')
    case 'FULFILLED':
      return withTone('已发货', 'sale')
    case 'CLOSED':
      return withTone('已关闭', 'draft')
    default:
      return withTone('待支付', 'draft')
  }
}

export function getPaymentChannelLabel(channel?: PaymentChannel) {
  if (channel === 'ALIPAY_PAGE') {
    return '支付宝支付'
  }

  if (channel === 'WECHAT_NATIVE') {
    return '微信支付'
  }

  return '未选择'
}

export function getPaymentChannelMeta(channel?: PaymentChannel): StatusMeta {
  if (channel === 'ALIPAY_PAGE') {
    return {
      label: '支付宝支付',
      className: 'status-pill status-pill--alipay',
    }
  }

  if (channel === 'WECHAT_NATIVE') {
    return {
      label: '微信支付',
      className: 'status-pill status-pill--wechat',
    }
  }

  return withTone('未选择', 'draft')
}

export function getPaymentStatusMeta(status: PaymentStatus): StatusMeta {
  switch (status) {
    case 'SUCCESS':
      return withTone('支付成功', 'sale')
    case 'CLOSED':
      return withTone('已关闭', 'draft')
    case 'FAILED':
      return withTone('支付异常', 'used')
    case 'PENDING':
      return withTone('等待支付', 'published')
    default:
      return withTone('待创建', 'draft')
  }
}

export function getOrderStatusMeta(status: OrderStatus): StatusMeta {
  switch (status) {
    case 'PAYING':
      return withTone('支付中', 'published')
    case 'PAID':
      return withTone('已支付', 'published')
    case 'FULFILLING':
      return withTone('发放中', 'published')
    case 'FULFILLED':
      return withTone('已完成', 'sale')
    case 'CLOSED':
      return withTone('已关闭', 'draft')
    case 'FAILED':
      return withTone('异常', 'used')
    default:
      return withTone('待支付', 'draft')
  }
}

export function getFulfillmentStatusMeta(status: FulfillmentStatus): StatusMeta {
  switch (status) {
    case 'PROCESSING':
      return withTone('发放中', 'published')
    case 'FULFILLED':
      return withTone('已发货', 'sale')
    case 'FAILED':
      return withTone('发放异常', 'used')
    default:
      return withTone('待发货', 'draft')
  }
}

export function getFulfillmentItemTypeLabel(type: FulfillmentItemType) {
  return type === 'CARD_SECRET' ? '卡密' : '说明内容'
}
